import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { dateTime, humanise, money } from '../../lib/format.js'
import { Badge, Card, Empty, RowsSkeleton } from '../../components/ui.js'
import { ConfirmDialog, toast } from '../../components/feedback.js'
import { MailIcon, MegaphoneIcon, WhatsAppIcon } from '../../components/icons.js'
import type { CampaignView } from './types.js'

const STATUS_TONE: Record<CampaignView['status'], string> = { scheduled: 'info', running: 'brand', completed: 'ok', cancelled: 'warn' }

/**
 * Bulk campaigns and how far each has got. Polls only while something is
 * still scheduled or sending; counts are the server's GROUP BY over the
 * message rows, so "sent" here means the provider accepted it.
 */
export function CampaignsPanel({ canWrite }: { canWrite: boolean }): ReactNode {
  const queryClient = useQueryClient()
  const [cancelling, setCancelling] = useState<CampaignView | null>(null)

  const campaigns = useQuery({
    queryKey: ['abandoned-campaigns'],
    queryFn: () => api<{ campaigns: CampaignView[] }>('/v1/abandoned/campaigns'),
    refetchInterval: (query) => (query.state.data?.campaigns.some((campaign) => campaign.status === 'running' || campaign.status === 'scheduled') ? 3000 : false),
  })

  const cancel = useMutation({
    mutationFn: (id: string) => api<CampaignView>(`/v1/abandoned/campaigns/${id}/cancel`, { method: 'POST' }),
    onSuccess: (campaign) => {
      setCancelling(null)
      toast('ok', 'Campaign cancelled', `${campaign.counts.cancelled} message${campaign.counts.cancelled === 1 ? '' : 's'} will not be sent.`)
      void queryClient.invalidateQueries({ queryKey: ['abandoned-campaigns'] })
    },
    onError: (error) => toast('error', 'Could not cancel', (error as Error).message),
  })

  if (campaigns.isPending) return <Card><RowsSkeleton rows={5} /></Card>
  if (campaigns.error) return <Card><Empty error title="Could not load campaigns">{(campaigns.error as Error).message}</Empty></Card>

  const list = campaigns.data?.campaigns ?? []

  if (list.length === 0) {
    return (
      <Card>
        <Empty icon={<MegaphoneIcon size={24} />} title="No campaigns yet">
          Select checkouts on the Checkouts tab and choose WhatsApp or Email to start one.
        </Empty>
      </Card>
    )
  }

  return (
    <Card flush title="Campaigns" subtitle="The 20 most recent bulk sends.">
      <ul className="campaign-list">
        {list.map((campaign) => {
          const done = campaign.counts.sent + campaign.counts.delivered + campaign.counts.read + campaign.counts.failed + campaign.counts.skipped + campaign.counts.cancelled
          const total = Math.max(1, done + campaign.counts.queued + campaign.counts.processing)
          const sent = campaign.counts.sent + campaign.counts.delivered + campaign.counts.read
          const pct = Math.round((done / total) * 100)

          return (
            <li key={campaign.id}>
              <div className="campaign-list__head">
                <span className="feature__icon" style={{ ['--tone' as string]: campaign.channel === 'whatsapp' ? 'var(--ok)' : 'var(--info)' }}>
                  {campaign.channel === 'whatsapp' ? <WhatsAppIcon size={16} /> : <MailIcon size={16} />}
                </span>
                <span className="list__text">
                  <strong>{campaign.name}</strong>
                  <small>
                    {campaign.status === 'scheduled' ? `Scheduled for ${dateTime(campaign.scheduledAt)}` : `Started ${dateTime(campaign.startedAt ?? campaign.createdAt)}`}
                    {campaign.couponCode ? ` · coupon ${campaign.couponCode}` : ''}
                  </small>
                </span>
                <Badge tone={STATUS_TONE[campaign.status]}>{humanise(campaign.status)}</Badge>
                {canWrite && (campaign.status === 'running' || campaign.status === 'scheduled') && (
                  <button type="button" className="btn btn--sm" onClick={() => setCancelling(campaign)}>
                    Cancel
                  </button>
                )}
              </div>

              <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`${campaign.name} progress`}>
                <span style={{ width: `${pct}%` }} />
              </div>

              <div className="campaign-list__stats">
                <span><strong>{sent}</strong> sent</span>
                {campaign.channel === 'whatsapp' && <span><strong>{campaign.counts.delivered + campaign.counts.read}</strong> delivered</span>}
                {campaign.channel === 'whatsapp' && <span><strong>{campaign.counts.read}</strong> read</span>}
                <span><strong>{campaign.clicked}</strong> clicked</span>
                {campaign.counts.failed > 0 && <span className="text-bad"><strong>{campaign.counts.failed}</strong> failed</span>}
                {campaign.counts.skipped > 0 && <span><strong>{campaign.counts.skipped}</strong> skipped</span>}
                {campaign.counts.queued + campaign.counts.processing > 0 && <span><strong>{campaign.counts.queued + campaign.counts.processing}</strong> waiting</span>}
                <span className="muted">{money(campaign.spent)} spent of ~{money(campaign.estimatedCost)}</span>
              </div>
            </li>
          )
        })}
      </ul>

      <ConfirmDialog
        open={cancelling !== null}
        danger
        title="Cancel this campaign?"
        confirmLabel="Cancel campaign"
        pending={cancel.isPending}
        onClose={() => setCancelling(null)}
        onConfirm={() => cancelling && cancel.mutate(cancelling.id)}
        message="Messages that have not gone out yet are stopped. Messages already sent cannot be recalled."
      />
    </Card>
  )
}
